import React from 'react';
import { VaultKeTheme } from '../../utils/theme';

const PrivacyPolicy: React.FC = () => {
  const styles: { [key: string]: React.CSSProperties } = {
    page: {
      minHeight: '100vh',
      backgroundColor: VaultKeTheme.colors.background,
      color: VaultKeTheme.colors.text,
      padding: VaultKeTheme.spacing.xl,
    },
    container: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    header: {
      backgroundColor: VaultKeTheme.colors.surface,
      padding: VaultKeTheme.spacing.lg,
      borderRadius: VaultKeTheme.borderRadius.md,
      marginBottom: VaultKeTheme.spacing.xl,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: VaultKeTheme.shadows.sm,
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: VaultKeTheme.colors.text,
      fontSize: '24px',
      cursor: 'pointer',
      padding: VaultKeTheme.spacing.sm,
      borderRadius: VaultKeTheme.borderRadius.sm,
      transition: 'background-color 0.2s ease',
    },
    title: {
      ...VaultKeTheme.typography.h2,
      color: VaultKeTheme.colors.text,
      margin: 0,
    },
    content: {
      backgroundColor: VaultKeTheme.colors.surface,
      borderRadius: VaultKeTheme.borderRadius.md,
      padding: VaultKeTheme.spacing.xxl,
      boxShadow: VaultKeTheme.shadows.sm,
      maxHeight: '70vh',
      overflowY: 'auto',
    },
    introSection: {
      paddingBottom: VaultKeTheme.spacing.xl,
      borderBottom: `1px solid ${VaultKeTheme.colors.border}`,
      marginBottom: VaultKeTheme.spacing.xl,
    },
    lastUpdated: {
      ...VaultKeTheme.typography.caption,
      color: VaultKeTheme.colors.textSecondary,
      fontStyle: 'italic',
      marginBottom: VaultKeTheme.spacing.md,
    },
    introText: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.text,
      lineHeight: '1.6',
    },
    section: {
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    sectionTitle: {
      ...VaultKeTheme.typography.h3,
      color: VaultKeTheme.colors.primary,
      marginBottom: VaultKeTheme.spacing.lg,
      fontWeight: 'bold',
    },
    sectionContent: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.text,
      lineHeight: '1.7',
      textAlign: 'justify',
    },
    bulletList: {
      marginLeft: VaultKeTheme.spacing.xl,
      marginTop: VaultKeTheme.spacing.sm,
    },
    bulletItem: {
      marginBottom: VaultKeTheme.spacing.sm,
      display: 'flex',
      alignItems: 'flex-start',
      gap: VaultKeTheme.spacing.sm,
    },
    bullet: {
      color: VaultKeTheme.colors.primary,
      fontWeight: 'bold',
      flexShrink: 0,
    },
    footer: {
      borderTop: `1px solid ${VaultKeTheme.colors.border}`,
      paddingTop: VaultKeTheme.spacing.xl,
      marginTop: VaultKeTheme.spacing.xxl,
    },
    footerText: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
      textAlign: 'center',
      fontStyle: 'italic',
    },
  };

  const sections = [
    {
      title: "1. INTRODUCTION",
      content: "VaultKe is a digital financial platform that facilitates chama (investment group) management, savings, loans, and financial services in Kenya. This Privacy Policy explains how we collect, use, protect, and share your personal information in compliance with the Data Protection Act, 2019 of Kenya and other applicable laws.",
      additional: "By using VaultKe, you consent to the collection and use of your information as described in this policy."
    },
    {
      title: "2. INFORMATION WE COLLECT",
      content: "Personal Information:",
      bullets: [
        "Full name, email address, phone number",
        "Date of birth and gender",
        "Physical address and location data",
        "Employment information and income details",
        "Mpesa TILL and PAYBILL details and mobile money information"
      ],
      additional: "Financial Information:",
      additionalBullets: [
        "Transaction history and payment records",
        "Credit history and loan repayment data",
        "Savings and investment records",
        "Chama membership and contribution history"
      ],
      technical: "Technical Information:",
      technicalBullets: [
        "Device information and unique identifiers",
        "IP address and location data",
        "App usage patterns and preferences",
        "Communication records within the platform"
      ]
    },
    {
      title: "3. HOW WE USE YOUR INFORMATION",
      content: "We use your information to:",
      bullets: [
        "Provide chama management and financial services",
        "Process transactions and maintain accurate records",
        "Verify your identity (KYC compliance)",
        "Assess creditworthiness for loans",
        "Send notifications about your account and transactions",
        "Comply with Central Bank of Kenya (CBK) regulations",
        "Prevent fraud and ensure platform security",
        "Improve our services and user experience",
        "Communicate important updates and offers"
      ]
    },
    {
      title: "4. LEGAL BASIS FOR PROCESSING",
      content: "Under the Data Protection Act, 2019, we process your data based on:",
      bullets: [
        "Consent: When you agree to use our services",
        "Contract: To fulfill our obligations under user agreements",
        "Legal Obligation: To comply with CBK, KRA, and other regulatory requirements",
        "Legitimate Interest: To prevent fraud and improve services",
        "Vital Interest: To protect your financial security"
      ]
    },
    {
      title: "5. DATA SHARING AND DISCLOSURE",
      content: "We may share your information with:",
      bullets: [
        "Chama members (limited to relevant financial data)",
        "Payment processors and banks for transactions",
        "Credit reference bureaus (with your consent)",
        "Regulatory authorities (CBK, FRC, DCI) when required by law",
        "Service providers under strict confidentiality agreements",
        "Law enforcement agencies when legally required"
      ],
      additional: "We DO NOT sell your personal data to third parties."
    },
    {
      title: "6. DATA SECURITY",
      content: "We implement robust security measures:",
      bullets: [
        "End-to-end encryption for sensitive data",
        "Secure servers hosted in certified data centers",
        "Multi-factor authentication",
        "Regular security audits and penetration testing",
        "Staff training on data protection",
        "Incident response procedures"
      ],
      additional: "Despite our efforts, no system is 100% secure. We will notify you of any significant data breaches within 72 hours as required by law."
    }
  ];

  const renderSection = (section: any, index: number) => (
    <div key={index} style={styles.section}>
      <h3 style={styles.sectionTitle}>{section.title}</h3>
      <p style={styles.sectionContent}>{section.content}</p>

      {section.bullets && (
        <ul style={styles.bulletList}>
          {section.bullets.map((bullet: string, bulletIndex: number) => (
            <li key={bulletIndex} style={styles.bulletItem}>
              <span style={styles.bullet}>•</span>
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      )}

      {section.additional && (
        <div style={{ marginTop: VaultKeTheme.spacing.lg }}>
          <p style={styles.sectionContent}>{section.additional}</p>
          {section.additionalBullets && (
            <ul style={styles.bulletList}>
              {section.additionalBullets.map((bullet: string, bulletIndex: number) => (
                <li key={bulletIndex} style={styles.bulletItem}>
                  <span style={styles.bullet}>•</span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {section.technical && (
        <div style={{ marginTop: VaultKeTheme.spacing.lg }}>
          <p style={styles.sectionContent}>{section.technical}</p>
          {section.technicalBullets && (
            <ul style={styles.bulletList}>
              {section.technicalBullets.map((bullet: string, bulletIndex: number) => (
                <li key={bulletIndex} style={styles.bulletItem}>
                  <span style={styles.bullet}>•</span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ ...styles.page, paddingBottom: '80px' }}>
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={() => window.history.back()}>
            ←
          </button>
          <h1 style={styles.title}>Privacy Policy</h1>
          <div style={{ width: '40px' }}></div>
        </div>

        <div style={styles.content}>
          <div style={styles.introSection}>
            <p style={styles.lastUpdated}>Last Updated: January 2025</p>
            <p style={styles.introText}>
              Your privacy is important to us. This policy explains how VaultKe handles your personal information in accordance with Kenyan law.
            </p>
          </div>

          {sections.map(renderSection)}

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>7. YOUR RIGHTS</h3>
            <p style={styles.sectionContent}>
              Under the Data Protection Act, 2019, you have the right to:
            </p>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Access your personal data</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Correct inaccurate information</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Delete your data (subject to legal requirements)</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Restrict processing of your data</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Data portability</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Object to processing</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Withdraw consent at any time</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Lodge a complaint with the Data Protection Commissioner</span></li>
            </ul>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>8. DATA RETENTION</h3>
            <p style={styles.sectionContent}>
              We retain your data for as long as necessary to provide services and comply with legal obligations:
            </p>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Account information: Duration of account plus 7 years</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Transaction records: 7 years (CBK requirement)</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Communication records: 3 years</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Marketing data: Until you opt out</span></li>
            </ul>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>9. INTERNATIONAL TRANSFERS</h3>
            <p style={styles.sectionContent}>
              Your data is primarily stored in Kenya. If we transfer data internationally, we ensure adequate protection through:
            </p>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Adequacy decisions by the Data Protection Commissioner</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Standard contractual clauses</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Binding corporate rules</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Your explicit consent</span></li>
            </ul>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>10. CONTACT INFORMATION</h3>
            <p style={styles.sectionContent}>
              For privacy-related inquiries, contact our Data Protection Officer:
            </p>
            <div style={{ marginLeft: VaultKeTheme.spacing.xl, marginTop: VaultKeTheme.spacing.sm }}>
              <p style={styles.sectionContent}><strong>Email:</strong> privacy@vaultke.com</p>
              <p style={styles.sectionContent}><strong>Phone:</strong> +254 700 000 000</p>
              <p style={styles.sectionContent}><strong>Address:</strong> VaultKe Limited, Kisumu, Kenya</p>
              <p style={styles.sectionContent}><strong>Data Protection Commissioner:</strong> www.odpc.go.ke</p>
            </div>
          </div>

          <div style={styles.footer}>
            <p style={styles.footerText}>
              This Privacy Policy is governed by the laws of Kenya. Any disputes will be resolved in Kenyan courts.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;